import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProjectSchema, type ClientDto, type ProjectDto } from '@taqon/contracts';
import { useNavigate, useSearchParams } from 'react-router';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { SearchFilterBar } from '../../shared/ui/filters';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { ImportButton } from '../../shared/import/ImportButton';
import { exportHref } from '../../shared/export/export-href';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';
import { ActiveClientPicker, ClientRef, Pager, PROJECTS } from '../crm/common';

const PAGE_SIZE = 50;

/**
 * Projects (ERD §4.2; FRD-CORE-006, FRD-CRM-033): a business and financial tracking master — code,
 * name, description, the client where applicable, and status. No tasks, milestones or planning
 * (FRD-CRM-034). Filters and the page are server-side and kept in the URL.
 */
export function ProjectsPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'projects.project:create');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState(params.get('search') ?? '');
  const status = params.get('status') ?? 'active';
  const term = params.get('search') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: 'status' | 'search' | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '' && key !== 'status') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: 'code',
  });
  if (status !== 'all') query.set('filter[status]', status);
  if (term !== '') query.set('filter[search]', term);
  const projects = useQuery({
    queryKey: ['projects', 'list', query.toString()],
    queryFn: () => getPage(`${PROJECTS}?${query.toString()}`, ProjectSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Projects"
        description="Projects as a business and financial tracking master, linked to a client where applicable."
        actions={
          <>
            <ExportButton recordType="projects.project" href={exportHref(PROJECTS, query)} />
            <ImportButton recordType="projects.project" />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCreating(true);
                }}
              >
                New project
              </Button>
            )}
          </>
        }
      />
      <SearchFilterBar
        aria-label="Project filters"
        label="Search by code or name"
        value={search}
        onChange={setSearch}
        onBlur={() => {
          if (search.trim() !== term) set('search', search.trim());
        }}
        onSubmit={() => {
          set('search', search.trim());
        }}
      >
        <TextField
          select
          size="small"
          label="Status"
          // "All" is the empty value; without these the select would show it blank.
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          value={status === 'all' ? '' : status}
          onChange={(e) => {
            set('status', e.target.value === '' ? 'all' : e.target.value);
          }}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="">All</MenuItem>
        </TextField>
      </SearchFilterBar>
      {projects.isError && <ErrorState message={projects.error.message} />}
      <Section>
        {projects.isPending && <LoadingState label="Loading projects" />}
        {projects.data?.items.length === 0 && <EmptyState title="No projects found" />}
        {projects.data !== undefined && projects.data.items.length > 0 && (
          <>
            <ProjectTable projects={projects.data.items} label="Projects" />
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={projects.data.total}
              noun="project(s)"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
      {creating && (
        <ProjectDialog
          onClose={(saved) => {
            setCreating(false);
            if (saved !== undefined) void navigate(`/projects/${saved.id}`);
          }}
        />
      )}
    </Stack>
  );
}

/** Projects in a table, each linked to its page; the client links where the session may. */
export function ProjectTable({
  projects,
  label,
}: {
  projects: readonly ProjectDto[];
  label: string;
}) {
  return (
    <Table size="small" aria-label={label}>
      <TableHead>
        <TableRow>
          <TableCell>Code</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Client</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {projects.map((project) => (
          <TableRow key={project.id} hover>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <RecordLink to={`/projects/${project.id}`}>{project.code}</RecordLink>
            </TableCell>
            <TableCell>{project.name}</TableCell>
            <TableCell>
              {project.clientId === null ? (
                '—'
              ) : (
                <ClientRef id={project.clientId} label={project.clientLabel ?? 'Not visible'} />
              )}
            </TableCell>
            <TableCell>
              <StatusChip status={project.status} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Create (no `project`) or edit a project. `client` preselects the client for "New project for
 * this client". The code is the permanent identifier; an edit changes name, description and the
 * client, sending only what changed with If-Match.
 */
export function ProjectDialog({
  project,
  client,
  onClose,
}: {
  project?: ProjectDto;
  client?: ClientDto;
  onClose: (saved?: ProjectDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canSeeClients = hasCapability(session.data, 'crm.client:view');
  const creating = project === undefined;
  const [code, setCode] = useState('');
  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [chosen, setChosen] = useState<ClientDto | null>(client ?? null);
  const [clientLink, setClientLink] = useState<'keep' | 'clear'>('keep');

  const body: Record<string, string | null> = {};
  if (creating) {
    body.code = code.trim();
    body.name = name.trim();
    if (description.trim() !== '') body.description = description.trim();
    if (chosen !== null) body.clientId = chosen.id;
  } else {
    if (name.trim() !== project.name) body.name = name.trim();
    if (description.trim() !== (project.description ?? '')) {
      body.description = description.trim() === '' ? null : description.trim();
    }
    if (chosen !== null && chosen.id !== project.clientId) body.clientId = chosen.id;
    if (clientLink === 'clear' && project.clientId !== null) body.clientId = null;
  }
  const complete = name.trim() !== '' && (!creating || code.trim() !== '');

  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(PROJECTS, body, ProjectSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${PROJECTS}/${project.id}`,
            body,
            ProjectSchema,
            session.data?.csrfToken ?? '',
            project.version,
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose(saved);
    },
  });

  return (
    <FormDialog
      open
      title={creating ? 'New project' : `Edit project ${project.code}`}
      submitLabel={creating ? 'Create project' : 'Save changes'}
      canSubmit={complete && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {creating ? (
        <TextField
          label="Code"
          required
          autoFocus
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
          }}
          helperText="Upper case, up to 20 characters, starting with a letter."
        />
      ) : (
        <TextField label="Code" value={project.code} disabled />
      )}
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <TextField
        label="Description"
        multiline
        minRows={2}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
      />
      {canSeeClients ? (
        <Box>
          <ActiveClientPicker
            label={!creating && project.clientId !== null ? 'Change client' : 'Client'}
            value={chosen}
            onChange={(value) => {
              setChosen(value);
              setClientLink('keep');
            }}
            helperText={
              !creating && project.clientLabel !== null
                ? `Currently ${project.clientLabel}`
                : 'Optional: the client the project is for.'
            }
          />
          {!creating && project.clientId !== null && (
            <TextField
              select
              fullWidth
              sx={{ mt: 2 }}
              label="Client link"
              value={clientLink}
              onChange={(e) => {
                setClientLink(e.target.value === 'clear' ? 'clear' : 'keep');
                if (e.target.value === 'clear') setChosen(null);
              }}
            >
              <MenuItem value="keep">Keep the client</MenuItem>
              <MenuItem value="clear">Remove the client</MenuItem>
            </TextField>
          )}
        </Box>
      ) : (
        <Alert severity="info" variant="outlined">
          Choosing a client needs permission to view clients.
        </Alert>
      )}
    </FormDialog>
  );
}
